import React, { type RefObject, useCallback, useEffect, useRef } from 'react';

export type InfiniteScrollerProps = {
	loadMore: () => void;
	children: JSX.Element;
	hasMore?: boolean;
	loader?: JSX.Element;
	isLoading: boolean;
};

const InfiniteScroller = ({
	hasMore,
	children,
	loadMore,
	loader,
	isLoading,
}: InfiniteScrollerProps) => {
	const loadMoreRef: RefObject<HTMLDivElement> = useRef(null);

	const fetchMoreCallback = useCallback(
		([loadMoreEntry]: IntersectionObserverEntry[]) => {
			// Automatically load more data when our invisible "load more" div is within the viewport
			if (loadMoreEntry.isIntersecting && hasMore && !isLoading) {
				loadMore();
			}
		},
		[loadMore, hasMore, isLoading],
	);

	useEffect(() => {
		const intersectionObserver = new IntersectionObserver(fetchMoreCallback);

		if (loadMoreRef.current) {
			intersectionObserver.observe(loadMoreRef.current);
		}

		return () => {
			intersectionObserver.disconnect();
		};
	}, [loadMoreRef, fetchMoreCallback]);

	return (
		<>
			{children}
			{hasMore && <div ref={loadMoreRef} />}
			{hasMore && loader}
		</>
	);
};

export default InfiniteScroller;
